import DataType from '@opsbase/lib-datatype/DataType/DataType';
import { flagColor } from '@opsbase/lib-issue/Flag/Flag';
import { issueStatusColor, issueStatusLabel } from '@opsbase/lib-issue/IssueStatus/IssueStatus';
import { Pages } from '@opsbase/lib-pages/Pages';
import useSelf from '@opsbase/react-ctrl-self/useSelf/useSelf';
import DataPill from '@opsbase/web-chips/DataPill/DataPill';
import SPTChip from '@opsbase/web-chips/SPTChip';
import FlagIcon from '@opsbase/web-icons/icons/FlagIcon';
import Pane from '@opsbase/web-layout/Pane';
import { Link } from '@opsbase/web-nav/Link';
import useNav from '@opsbase/web-nav/useNav';
import IconTitle from '@opsbase/web-ui/IconTitle/IconTitle';
import moment from 'moment-timezone';
import React, { useCallback, useContext, useMemo } from 'react';
import useReportCSS from '../../useReportCSS';
import DetailsReportSwab from '../DetailsReportSwab';
import DetailsIssueReportContext from './DetailsIssueReportContext';
const dateFormat = 'ddd, MMM D YYYY';
const timeFormat = 'h:mm A';
function DateBit({ date }) {
    if (!date)
        return null;
    return (React.createElement(React.Fragment, null,
        moment(date).format(dateFormat),
        React.createElement("br", null),
        moment(date).format(timeFormat)));
}
const reportFields = [
    {
        title: 'Assignee',
        type: DataType.SPT,
        value: issue => issue,
    },
    {
        title: 'Place',
        type: DataType.PLACE,
        value: issue => { var _a; return (_a = issue === null || issue === void 0 ? void 0 : issue.place) === null || _a === void 0 ? void 0 : _a.title; },
    },
    {
        left: { title: 'Status', type: 'issue_status', value: issue => issue === null || issue === void 0 ? void 0 : issue.status },
        right: {
            title: 'Deadline Date',
            type: 'date',
            value: issue => deadlineDateInPlaceTimeZone(issue),
        },
    },
    {
        title: 'Associated Op',
        type: 'link',
        value: issue => { var _a; return (_a = issue === null || issue === void 0 ? void 0 : issue.op) === null || _a === void 0 ? void 0 : _a.title; },
        metadata: Pages.OP_VIEW,
    },
    {
        left: {
            title: 'Type',
            type: 'string',
            value: (issue, settings) => settings.getIssueTypeTitle(issue === null || issue === void 0 ? void 0 : issue.type),
        },
        right: {
            title: 'Flag',
            type: 'flag',
            value: issue => issue.flag,
        },
    },
    {
        title: 'Asset',
        type: 'string',
        value: issue => { var _a; return (_a = issue === null || issue === void 0 ? void 0 : issue.asset) === null || _a === void 0 ? void 0 : _a.title; },
    },
    {
        title: 'Reported Date',
        type: 'date',
        value: issue => occurenceDateInPlaceTimeZone(issue),
    },
    {
        title: 'Reported By',
        type: DataType.STAFF,
        value: issue => issue === null || issue === void 0 ? void 0 : issue.createdBy,
    },
    {
        title: 'Closed Date',
        type: 'date',
        value: issue => closedDateInPlaceTimeZone(issue),
    },
    {
        title: 'Closed By',
        type: DataType.STAFF,
        value: issue => issue === null || issue === void 0 ? void 0 : issue.closedBy,
    },
    {
        title: 'Cause(s)',
        type: 'string',
        value: issue => issue.causes.join(', '),
        capitalize: false,
    },
    {
        title: 'Corrective Action(s)',
        type: 'string',
        value: issue => issue.correctiveActions.join(', '),
        capitalize: false,
    },
    {
        title: 'Preventative Action(s)',
        type: 'string',
        value: issue => issue.preventativeActions.join(', '),
        capitalize: false,
    },
    {
        title: 'Associated Integrations',
        type: 'string',
        value: issue => issue.associatedIntegrationId ? `IQM ${issue.associatedIntegrationId}` : null,
        capitalize: false,
    },
];
const DetailsIssueReportSummary = () => {
    const { issue, def } = useContext(DetailsIssueReportContext);
    const { settings } = useSelf();
    const myCss = useReportCSS(def.pdf);
    const { goTo } = useNav();
    const onClickField = useCallback((field, value) => {
        if (field.type === 'link') {
            goTo(field.metadata, { id: value });
        }
    }, [goTo]);
    const getValueViewForASimpleField = useCallback((field, v) => {
        switch (field.type) {
            case 'date':
                return React.createElement(DateBit, { date: v });
            case DataType.PLACE:
                return React.createElement(DataPill, { type: DataType.PLACE, value: v });
            case DataType.ASSET:
                return React.createElement(DataPill, { type: DataType.ASSET, value: v });
            case 'link':
                const onClickHandler = () => onClickField(field, v);
                return (React.createElement(Link, { onClick: onClickHandler, noWrap: true }, v));
            case 'issue_status':
                return (React.createElement(React.Fragment, null,
                    React.createElement(DetailsReportSwab, { color: issueStatusColor(v) }),
                    issueStatusLabel(v)));
            case 'string':
                return v;
            case 'flag':
                return (React.createElement(IconTitle, { title: settings.getFlagName(v), icon: React.createElement(FlagIcon, { htmlColor: flagColor(v).main, size: 18 }) }));
            case DataType.STAFF:
                return (React.createElement(DataPill, { value: v.title, type: DataType.STAFF, avatarUrl: v.profileImageUrl }));
            case DataType.SPT:
                return React.createElement(SPTChip, { item: issue, prefix: "assignedTo" });
        }
    }, [issue, onClickField, settings]);
    const getRowForSimpleField = useCallback((field) => {
        const v = field.value(issue, settings);
        if (!v)
            return null;
        return (React.createElement(React.Fragment, null,
            React.createElement("th", { className: myCss.heading + ' ' + myCss.left }, field.title),
            React.createElement("td", { colSpan: 3, className: myCss.data + ' ' + myCss.right, style: field.capitalize ? { textTransform: 'capitalize' } : undefined }, getValueViewForASimpleField(field, v))));
    }, [
        getValueViewForASimpleField,
        issue,
        myCss.data,
        myCss.heading,
        myCss.left,
        myCss.right,
        settings,
    ]);
    const getRowForDualField = useCallback((f) => {
        if (!f.left)
            return null;
        const leftV = f.left.value(issue, settings);
        const rightV = f.right.value(issue, settings);
        if (!leftV && !rightV)
            return null;
        if (!rightV)
            return getRowForSimpleField(f.left);
        if (!leftV)
            return getRowForSimpleField(f.right);
        return (React.createElement(React.Fragment, null,
            React.createElement("th", { className: myCss.heading + ' ' + myCss.left }, f.left.title),
            React.createElement("td", { className: myCss.data }, getValueViewForASimpleField(f.left, leftV)),
            React.createElement("th", { className: myCss.heading + ' ' + myCss.right }, f.right.title),
            React.createElement("td", { className: myCss.data }, getValueViewForASimpleField(f.right, rightV))));
    }, [
        getRowForSimpleField,
        getValueViewForASimpleField,
        issue,
        myCss.data,
        myCss.heading,
        myCss.left,
        myCss.right,
        settings,
    ]);
    const views = useMemo(() => {
        return reportFields.map(field => {
            let content;
            const f = field;
            if (f.left) {
                content = getRowForDualField(f);
            }
            else {
                content = getRowForSimpleField(field);
            }
            return content ? React.createElement("tr", { className: myCss.row }, content) : null;
        });
    }, [getRowForDualField, getRowForSimpleField, myCss.row]);
    return (React.createElement("div", { className: myCss.page2 },
        React.createElement(Pane, null,
            React.createElement("div", { className: myCss.heading1 }, "Summary"),
            React.createElement("table", { className: myCss.table },
                React.createElement("tbody", null, views)))));
};
export default DetailsIssueReportSummary;
function deadlineDateInPlaceTimeZone(issue) {
    var _a;
    return issue.deadlineDate
        ? ((_a = issue.place) === null || _a === void 0 ? void 0 : _a.timezone)
            ? moment(issue.deadlineDate).tz(issue.place.timezone)
            : moment(issue.deadlineDate)
        : undefined;
}
function occurenceDateInPlaceTimeZone(issue) {
    var _a;
    return issue.occurrenceDate
        ? ((_a = issue.place) === null || _a === void 0 ? void 0 : _a.timezone)
            ? moment(issue.occurrenceDate).tz(issue.place.timezone)
            : moment(issue.occurrenceDate)
        : undefined;
}
function closedDateInPlaceTimeZone(issue) {
    var _a;
    return issue.closedDate
        ? ((_a = issue.place) === null || _a === void 0 ? void 0 : _a.timezone)
            ? moment(issue.closedDate).tz(issue.place.timezone)
            : moment(issue.closedDate)
        : undefined;
}
